import { Box, Button, Typography, TextField, Autocomplete } from '@mui/material';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import axiosService from 'utils/axios';
import { Choice, Vehicle, VEHICLE_FIELDS } from 'types/vehicles/types';
import { WAREHOUSE_DELIVERY_DISPATCH, WAREHOUSE_DELIVERY_LABEL_MAP } from '../../config';
import { Account, Carrier, Location } from 'types/entities/types';
import { User } from 'types/auth/types';
import AddOrEditUser from 'pages/users/list/AddOrEditUser';
import AddOrEditAccount from 'pages/entities/accounts/AddOrEditAccount';
import AddOrEditLocation from 'pages/entities/locations/AddOrEditLocation';
import AddOrEditCarrier from 'pages/entities/carriers/AddOrEditCarrier';
import { vehiclePageStyles } from '../styles';
import '../styles.css';

interface VehicleDropdownOption {
  id: string;
  name: string;
}

export interface VehicleGeneralSectionData {
  account_id: string | null;
  location_id: string | null;
  stock: string;
  vendor: string;
  warehouse_delivery: string | number | undefined;
  status: string | number;
  client_id: string | null;
  carrier_id: string | null;
}

interface VehicleGeneralSectionProps {
  updateSubmitData: (data: VehicleGeneralSectionData) => void;
  canView: (fieldName: string) => boolean;
  canEdit: (fieldName: string) => boolean;
  isSuperuser: boolean;
  newEntryStatus: number;
  newEntryStatusLabel: string;
  user: User | null;
  readOnly?: boolean;
}

export interface VehicleGeneralSectionRef {
  updateSectionData: (vehicleData: Vehicle) => void;
}

const VehicleGeneralSection = React.forwardRef(
  (
    {
      updateSubmitData,
      canView,
      canEdit,
      isSuperuser,
      newEntryStatus,
      newEntryStatusLabel,
      user,
      readOnly,
    }: VehicleGeneralSectionProps,
    ref
  ) => {
    // Vehicle properties
    const [stockInput, setStockInput] = useState<string>('');
    const [vendorInput, setVendorInput] = useState<string>('');
    const [warehouseDeliveryChoice, setWarehouseDeliveryChoice] = useState<Choice | null>({
      value: WAREHOUSE_DELIVERY_DISPATCH,
      // eslint-disable-next-line camelcase
      display_name: WAREHOUSE_DELIVERY_LABEL_MAP[WAREHOUSE_DELIVERY_DISPATCH],
    });
    const [statusChoice, setStatusChoice] = useState<Choice>({
      value: newEntryStatus,
      // eslint-disable-next-line camelcase
      display_name: newEntryStatusLabel,
    });
    const [clientChoice, setClientChoice] = useState<VehicleDropdownOption | null>(null);

    const [accountTextInput, setAccountTextInput] = useState<string>();
    const [accountChoice, setAccountChoice] = useState<VehicleDropdownOption | null>(null);
    const [locationTextInput, setLocationTextInput] = useState<string>();
    const [locationChoice, setLocationChoice] = useState<VehicleDropdownOption | null>(null);

    const [carrierChoice, setCarrierChoice] = useState<VehicleDropdownOption | null>(null);

    // Account options
    const [accountOptions, setAccountOptions] = useState<VehicleDropdownOption[]>([]);
    const [isAccountOptionsLoading, setIsAccountOptionsLoading] = useState(false);

    // Location options
    const [locationOptions, setLocationOptions] = useState<VehicleDropdownOption[]>([]);
    const [isLocationOptionsLoading, setIsLocationOptionsLoading] = useState(false);

    // Status and warehouse delivery options
    const [statusOptions, setStatusOptions] = useState<Choice[]>([]);
    const [warehouseDeliveryOptions, setWarehouseDeliveryOptions] = useState<Choice[]>([]);
    const [isVehicleOptionsLoading, setIsVehicleOptionsLoading] = useState(false);

    // Carrier options
    const [carrierOptions, setCarrierOptions] = useState<VehicleDropdownOption[]>([]);
    const [isCarrierOptionsLoading, setIsCarrierOptionsLoading] = useState(false);

    // Client options
    const [clientTextInput, setClientTextInput] = useState<string>();
    const [clientOptions, setClientOptions] = useState<VehicleDropdownOption[]>([]);
    const [isClientOptionsLoading, setIsClientOptionsLoading] = useState(false);

    // Create Entities states
    const [addNewAccountModalOpen, setAddNewAccountModalOpen] = useState(false);
    const [addNewLocationModalOpen, setAddNewLocationModalOpen] = useState(false);
    const [addNewCarrierModalOpen, setAddNewCarrierModalOpen] = useState(false);
    const [addNewUserModalOpen, setAddNewUserModalOpen] = useState(false);

    React.useEffect(() => {
      // When one of the internal states change, update the submit data
      updateSubmitData({
        // eslint-disable-next-line camelcase
        account_id: accountChoice?.id || null,
        // eslint-disable-next-line camelcase
        location_id: locationChoice?.id || null,
        stock: stockInput,
        vendor: vendorInput,
        // eslint-disable-next-line camelcase
        warehouse_delivery: warehouseDeliveryChoice?.value,
        // eslint-disable-next-line camelcase
        status: statusChoice?.value,
        // eslint-disable-next-line camelcase
        client_id: clientChoice?.id || null,
        // eslint-disable-next-line camelcase
        carrier_id: carrierChoice?.id || null,
      });
    }, [
      statusChoice,
      clientChoice,
      vendorInput,
      accountChoice,
      locationChoice,
      stockInput,
      carrierChoice,
      warehouseDeliveryChoice,
      updateSubmitData,
    ]);

    React.useImperativeHandle(ref, () => ({
      updateSectionData: (vehicleData: Vehicle) => {
        // STATUS
        setStatusChoice(
          vehicleData?.status || {
            value: newEntryStatus,
            // eslint-disable-next-line camelcase
            display_name: newEntryStatusLabel,
          }
        );

        // CLIENT
        if (vehicleData?.client) {
          setClientChoice({
            id: vehicleData.client.id,
            name: `${vehicleData.client.first_name} ${vehicleData.client.last_name}`,
          });
        } else if (!isSuperuser && user) {
          // Client adding a new vehicle, set client choice to the user himself
          setClientChoice({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`.trim(),
          });
        } else {
          setClientChoice(null);
        }

        // VENDOR
        setVendorInput(vehicleData?.vendor);

        // ACCOUNT
        setAccountChoice(
          vehicleData?.account
            ? {
                id: vehicleData.account.id,
                name: vehicleData.account.name,
              }
            : null
        );

        // LOCATION
        setLocationChoice(
          vehicleData?.location
            ? {
                id: vehicleData.location.id,
                name: vehicleData.location.name,
              }
            : null
        );

        // STOCK
        setStockInput(vehicleData?.stock);

        // CARRIER
        setCarrierChoice(
          vehicleData?.carrier
            ? {
                id: vehicleData.carrier.id,
                name: vehicleData.carrier.company.name,
              }
            : null
        );

        // WAREHOUSE DELIVERY
        setWarehouseDeliveryChoice(
          vehicleData?.warehouse_delivery || {
            value: WAREHOUSE_DELIVERY_DISPATCH,
            // eslint-disable-next-line camelcase
            display_name: WAREHOUSE_DELIVERY_LABEL_MAP[WAREHOUSE_DELIVERY_DISPATCH],
          }
        );
      },
    }));

    const getAccountsList = useCallback((onSuccess?: () => void) => {
      setIsAccountOptionsLoading(true);
      axiosService
        .get(`/entities/account/`)
        .then(res => {
          const names: VehicleDropdownOption[] = [];
          res.data.forEach((account: Account) => names.push({ id: account.id, name: account.name }));
          setAccountOptions(names);
          if (onSuccess) {
            onSuccess();
          }
        })
        .finally(() => setIsAccountOptionsLoading(false));
    }, []);

    const getLocationsList = useCallback((onSuccess?: () => void) => {
      setIsLocationOptionsLoading(true);
      axiosService
        .get(`/entities/location/`)
        .then(res => {
          const names: VehicleDropdownOption[] = [];
          res.data.forEach((location: Location) => names.push({ id: location.id, name: location.name }));
          setLocationOptions(names);
          if (onSuccess) {
            onSuccess();
          }
        })
        .finally(() => setIsLocationOptionsLoading(false));
    }, []);

    const getStatusOptions = useCallback(() => {
      setIsVehicleOptionsLoading(true);
      axiosService
        .options(`/vehicles/vehicle/`)
        .then(res => {
          setStatusOptions(res.data.actions.POST?.status?.choices);
          setWarehouseDeliveryOptions(res.data.actions.POST?.warehouse_delivery?.choices);
        })
        .finally(() => setIsVehicleOptionsLoading(false));
    }, []);

    const getClientsList = useCallback((onSuccess?: () => void) => {
      setIsClientOptionsLoading(true);
      axiosService
        .get(`/user/`)
        .then(res => {
          const names: VehicleDropdownOption[] = [];
          res.data.forEach((user: User) => names.push({ id: user.id, name: `${user.first_name} ${user.last_name}` }));
          setClientOptions(names);
          if (onSuccess) {
            onSuccess();
          }
        })
        .finally(() => setIsClientOptionsLoading(false));
    }, []);

    const getCarriersList = useCallback((onSuccess?: () => void) => {
      setIsCarrierOptionsLoading(true);
      axiosService
        .get(`/entities/carrier/`)
        .then(res => {
          const names: VehicleDropdownOption[] = [];
          res.data.forEach((carrier: Carrier) => names.push({ id: carrier.id, name: carrier.company.name }));
          setCarrierOptions(names);
          if (onSuccess) {
            onSuccess();
          }
        })
        .finally(() => setIsCarrierOptionsLoading(false));
    }, []);

    useEffect(() => {
      if (readOnly) return;

      if (isSuperuser) {
        getAccountsList();
        getCarriersList();
      }

      getLocationsList();
      getStatusOptions();
      getClientsList();
    }, [getAccountsList, getCarriersList, getClientsList, getLocationsList, getStatusOptions, isSuperuser, readOnly]);

    return (
      <Box sx={vehiclePageStyles.card}>
        <Box sx={vehiclePageStyles.cardHeader}>General</Box>
        {canView(VEHICLE_FIELDS.STATUS) && canEdit(VEHICLE_FIELDS.STATUS) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>
              Status{canEdit(VEHICLE_FIELDS.STATUS) ? ' *' : ''}
            </Typography>
            <Autocomplete
              value={statusChoice}
              disableClearable
              disabled={!canEdit(VEHICLE_FIELDS.STATUS)}
              onChange={(event, value) => setStatusChoice(value)}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.display_name}
              options={statusOptions}
              loading={isVehicleOptionsLoading}
              sx={vehiclePageStyles.detailTextField}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  variant="standard"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      ...vehiclePageStyles.detailTextFieldInput,
                    },
                  }}
                />
              )}
            />
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.STATUS) && !canEdit(VEHICLE_FIELDS.STATUS) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Status</Typography>
            <Box sx={vehiclePageStyles.detailValue}>{statusChoice.display_name || '-'}</Box>
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.CLIENT) ? (
          <>
            <Box sx={vehiclePageStyles.detailBox}>
              <Typography sx={vehiclePageStyles.detailTitle}>User</Typography>
              {canEdit(VEHICLE_FIELDS.CLIENT) ? (
                <Autocomplete
                  value={clientChoice}
                  onChange={(event, value) => setClientChoice(value)}
                  onInputChange={(event, value) => setClientTextInput(value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={option => option.name}
                  options={clientOptions}
                  loading={isClientOptionsLoading}
                  sx={vehiclePageStyles.detailTextField}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="standard"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          ...vehiclePageStyles.detailTextFieldInput,
                        },
                      }}
                    />
                  )}
                  noOptionsText={
                    isSuperuser ? (
                      <Button onClick={() => setAddNewUserModalOpen(true)}>Add a new Client (User)</Button>
                    ) : (
                      'No options'
                    )
                  }
                />
              ) : (
                <Box sx={vehiclePageStyles.detailValue}>{clientChoice?.name || '-'}</Box>
              )}
            </Box>
            {addNewUserModalOpen ? (
              <AddOrEditUser
                userName={clientTextInput}
                onSave={newUser => {
                  getClientsList(() => {
                    setClientChoice({
                      id: newUser.id,
                      name: `${newUser.first_name} ${newUser.last_name}`,
                    });
                    setAddNewUserModalOpen(false);
                  });
                }}
                modalTitle="Add new user"
                isModalOpen={addNewUserModalOpen}
                setModalOpen={setAddNewUserModalOpen}
              />
            ) : null}
          </>
        ) : null}
        {canView(VEHICLE_FIELDS.VENDOR) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Vendor</Typography>
            {canEdit(VEHICLE_FIELDS.VENDOR) ? (
              <TextField
                variant="standard"
                size="small"
                id="vendor"
                value={vendorInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setVendorInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextField}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{vendorInput || '-'}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.ACCOUNT) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Account (Buyer)</Typography>
            {canEdit(VEHICLE_FIELDS.ACCOUNT) ? (
              <Autocomplete
                value={accountChoice}
                onChange={(event, value) => setAccountChoice(value)}
                onInputChange={(event, value) => setAccountTextInput(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option => option.name}
                options={accountOptions}
                loading={isAccountOptionsLoading}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...vehiclePageStyles.detailTextFieldInput,
                      },
                    }}
                  />
                )}
                noOptionsText={
                  isSuperuser ? (
                    <Button onClick={() => setAddNewAccountModalOpen(true)}>Add a new Account</Button>
                  ) : (
                    'No options'
                  )
                }
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{accountChoice?.name || '-'}</Box>
            )}
          </Box>
        ) : null}
        {addNewAccountModalOpen ? (
          <AddOrEditAccount
            accountName={accountTextInput}
            onSave={newAccount => {
              getAccountsList(() => {
                setAccountChoice({
                  id: newAccount.id,
                  name: newAccount.name,
                });
                setAddNewAccountModalOpen(false);
              });
            }}
            modalTitle="Add new account"
            isModalOpen={addNewAccountModalOpen}
            setModalOpen={setAddNewAccountModalOpen}
          />
        ) : null}
        {canView(VEHICLE_FIELDS.LOCATION) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Location</Typography>
            {canEdit(VEHICLE_FIELDS.LOCATION) ? (
              <Autocomplete
                value={locationChoice}
                onChange={(event, value) => setLocationChoice(value)}
                onInputChange={(event, value) => setLocationTextInput(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option => option.name}
                options={locationOptions}
                loading={isLocationOptionsLoading}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...vehiclePageStyles.detailTextFieldInput,
                      },
                    }}
                  />
                )}
                noOptionsText={
                  isSuperuser ? (
                    <Button onClick={() => setAddNewLocationModalOpen(true)}>Add a new Location</Button>
                  ) : (
                    'No options'
                  )
                }
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{locationChoice?.name || '-'}</Box>
            )}
          </Box>
        ) : null}
        {addNewLocationModalOpen ? (
          <AddOrEditLocation
            locationName={locationTextInput}
            onSave={newLocation => {
              getLocationsList(() => {
                setLocationChoice({
                  id: newLocation.id,
                  name: newLocation.name,
                });
                setAddNewLocationModalOpen(false);
              });
            }}
            modalTitle="Add new location"
            isModalOpen={addNewLocationModalOpen}
            setModalOpen={setAddNewLocationModalOpen}
          />
        ) : null}
        {canView(VEHICLE_FIELDS.STOCK) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>
              Stock (Lot){canEdit(VEHICLE_FIELDS.STOCK) ? ' *' : ''}
            </Typography>
            {canEdit(VEHICLE_FIELDS.STOCK) ? (
              <TextField
                required
                variant="standard"
                size="small"
                id="stock"
                value={stockInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setStockInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextField}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{stockInput || '-'}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.CARRIER) ? (
          <>
            <Box sx={vehiclePageStyles.detailBox}>
              <Typography sx={vehiclePageStyles.detailTitle}>Carrier</Typography>
              {canEdit(VEHICLE_FIELDS.CARRIER) ? (
                <Autocomplete
                  value={carrierChoice}
                  onChange={(event, value) => setCarrierChoice(value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={option => option.name}
                  options={carrierOptions}
                  loading={isCarrierOptionsLoading}
                  sx={vehiclePageStyles.detailTextField}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="standard"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          ...vehiclePageStyles.detailTextFieldInput,
                        },
                      }}
                    />
                  )}
                  noOptionsText={
                    isSuperuser ? (
                      <Button onClick={() => setAddNewCarrierModalOpen(true)}>Add a new Carrier</Button>
                    ) : (
                      'No options'
                    )
                  }
                />
              ) : (
                <Box sx={vehiclePageStyles.detailValue}>{carrierChoice?.name || '-'}</Box>
              )}
            </Box>
            {addNewCarrierModalOpen ? (
              <AddOrEditCarrier
                onSave={newCarrier => {
                  getCarriersList(() => {
                    setCarrierChoice({
                      id: newCarrier.id,
                      name: newCarrier.company.name,
                    });
                    setAddNewCarrierModalOpen(false);
                  });
                }}
                modalTitle="Add new carrier"
                isModalOpen={addNewCarrierModalOpen}
                setModalOpen={setAddNewCarrierModalOpen}
              />
            ) : null}
          </>
        ) : null}
        {canView(VEHICLE_FIELDS.WAREHOUSE_DELIVERY) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Warehouse Delivery</Typography>
            {canEdit(VEHICLE_FIELDS.WAREHOUSE_DELIVERY) ? (
              <Autocomplete
                value={warehouseDeliveryChoice}
                onChange={(event, value) => setWarehouseDeliveryChoice(value)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={option => option.display_name}
                options={warehouseDeliveryOptions}
                loading={isVehicleOptionsLoading}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...vehiclePageStyles.detailTextFieldInput,
                      },
                    }}
                  />
                )}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{warehouseDeliveryChoice?.display_name || '-'}</Box>
            )}
          </Box>
        ) : null}
      </Box>
    );
  }
);

VehicleGeneralSection.displayName = 'VehicleGeneralSection';

export default VehicleGeneralSection;
